import type { Fleet, Session, UserRole, UUID } from '@cobliteam/common-types';
import { CookieManager } from '@cobliteam/cookie-manager';
import { permissions } from '@cobliteam/permissions';
import jwtDecode, { type JwtPayload } from 'jwt-decode';

export interface CobliJwtPayload extends JwtPayload {
  sessionId: UUID;
  userId: UUID;
  fullName: string;
  role: UserRole;
  emailAddress: string;
  groupsRestriction: string[];
  fleets: Fleet[];
  fleetId: UUID;
}

const COBLER_TEMPORARY_TOKEN = 'cobler-temporary-token';

export class TemporaryTokenManager {
  private static getDomain = () => {
    const isLocalhost = window.location.hostname.includes('localhost');
    return isLocalhost ? 'localhost' : 'cobli.co';
  };

  public static getTemporaryToken = () => CookieManager.getCookie(COBLER_TEMPORARY_TOKEN);

  public static setTemporaryToken = (jwt: string) => {
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + 8 * 60 * 60 * 1000); // 8 hours later

    const domain = this.getDomain();

    document.cookie = `cobler-temporary-token=${jwt}; path=/; domain=${domain}; expires=${expirationDate.toUTCString()}`;
  };

  public static deleteTemporaryToken = () => {
    if (this.getTemporaryToken() === undefined) return;

    const domain = this.getDomain();

    document.cookie = `${encodeURIComponent(COBLER_TEMPORARY_TOKEN)}=; max-age=0; domain=${domain}`;
  };

  public static buildSessionFromTemporaryToken(jwt: string): Session {
    const jwtPayload = jwtDecode<CobliJwtPayload>(jwt);

    // functionalities that depend on the user to exist in severino's database won't work
    const permissionsToExclude = ['create-update-delete:users-and-invites', 'update:fleet-info'];
    const permissionsToGrant = Object.values(permissions).filter((value) => !permissionsToExclude.includes(value));

    const session: Session = {
      id: jwtPayload.sessionId,
      user: {
        id: jwtPayload.userId,
        name: jwtPayload.fullName,
        role: jwtPayload.role,
        email: jwtPayload.emailAddress,
        groupsRestriction: jwtPayload.groupsRestriction,
        grantedPermissions: permissionsToGrant,
      },
      fleet: {
        id: jwtPayload.fleetId,
        name: jwtPayload.fleets[0].name,
        active: jwtPayload.fleets[0].active,
      },
      authenticationToken: jwt,
    };

    return session;
  }
}
